import { forwardRef, useImperativeHandle } from 'react';
import ValidationInfo from '../../../../validation/validation-info';
import NumberFieldFormGroup from '../../../Shared/NumberFieldFormGroup';
import CheckboxFormGroup from '../../../Shared/CheckboxFormGroup';
import SelectFormGroup from '../../../Shared/SelectFormGroup';
import { ZoneType } from '../../models/zone-type.enum';
import { TranslationService } from '../../../../services/translation.service';
import type IValidatable from '../../../Shared/validatable.interface';
import ValidationEngine from '../../../../validation/validation-engine';
import type { IServerValidationError } from '../../../Shared/validatable.interface';
import { TicketType } from '../../models/ticket-type.enum';
import { AnalyticsService } from '../../../../services/analytics-service';
import { TravelInformationField } from '../../models/travel-information-field.enum';
import { SanitizerService } from '../../../../services/sanitizer.service';

export interface ITravelInformation {
  travelDaysPerMonth: number | undefined;
  parkingFeeSekPerDay?: number;
  congestionTaxSekPerDay?: number;
  distanceFromHomeToWorkKmState: number | undefined;
  prefferredTicketType: TicketType;
  travelZones: ZoneType[];
  travelDeductionSekPerYear?: number | undefined;
}
export interface ITravelInformationState {
  value: ITravelInformation;
  validationInfo: ValidationInfo;
}

export interface TravelInformationProps {
  state: ITravelInformationState;
  onChange?: (value: ITravelInformation, field: TravelInformationField) => void;
  showDetailsInformation?: boolean;
  className?: string;
}

export default forwardRef<IValidatable, TravelInformationProps>(function TravelInformation({ state, onChange, showDetailsInformation = false, className = undefined }, ref) {
  // === HELPER FUNCTIONS ===
  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      const travelDaysPerMonthkValidationInfo = new ValidationInfo(`${TranslationService.get('pricecalculatorpage.travelinformation.traveldayspermonth')} - `);
      const parkingFeeSekPerDayValidationInfo = new ValidationInfo(`${TranslationService.get('pricecalculatorpage.carinformation.parkingfeesekperday')} - `);
      const congestionTaxSekPerDayValidationInfo = new ValidationInfo(`${TranslationService.get('pricecalculatorpage.carinformation.congestiontaxsekperday')} - `);
      const distanceFromHomeToWorkKmValidationInfo = new ValidationInfo(`${TranslationService.get('pricecalculatorpage.travelinformation.distancefromhometowork')} - `);
      const travelZonesValidationInfo = new ValidationInfo(`${TranslationService.get('pricecalculatorpage.travelinformation.travelzones')} - `);
      const travelDeductionSekPerYearValidationInfo = new ValidationInfo(`${TranslationService.get('pricecalculatorpage.carinformation.traveldeductionsekperyear')} - `);

      ValidationEngine.for(travelDaysPerMonthkValidationInfo)
        .required(state.value.travelDaysPerMonth, 'pricecalculatorpage.anyrequired')
        .greaterThanOrEqual(state.value.travelDaysPerMonth ?? 0, 1, 'pricecalculatorpage.travelinformation.traveldaysvalidationerror')
        .lessThan(state.value.travelDaysPerMonth ?? 0, 32, 'pricecalculatorpage.travelinformation.traveldaysvalidationerror')
        .validate();
      ValidationEngine.for(parkingFeeSekPerDayValidationInfo)
        .greaterThanOrEqual(state.value.parkingFeeSekPerDay ?? 0, 0, 'pricecalculatorpage.valuegreaterorequaltozeroerror')
        .validate();
      ValidationEngine.for(congestionTaxSekPerDayValidationInfo)
        .greaterThanOrEqual(state.value.congestionTaxSekPerDay ?? 0, 0, 'pricecalculatorpage.valuegreaterorequaltozeroerror')
        .validate();
      ValidationEngine.for(distanceFromHomeToWorkKmValidationInfo)
        .required(state.value.distanceFromHomeToWorkKmState, 'pricecalculatorpage.anyrequired')
        .greaterThanOrEqual(state.value.distanceFromHomeToWorkKmState ?? 0, 0, 'pricecalculatorpage.valuegreaterorequaltozeroerror')
        .validate();
      ValidationEngine.for(travelZonesValidationInfo).required(state.value.travelZones, 'pricecalculatorpage.anyrequired').notEmpty(state.value.travelZones).validate();
      ValidationEngine.for(travelDeductionSekPerYearValidationInfo)
        .greaterThanOrEqual(state.value.travelDeductionSekPerYear ?? 0, 0, 'pricecalculatorpage.valuegreaterorequaltozeroerror')
        .validate();

      return new ValidationInfo('', [], {
        travelDaysPerMonth: travelDaysPerMonthkValidationInfo,
        distanceFromHomeToWorkKm: distanceFromHomeToWorkKmValidationInfo,
        travelZones: travelZonesValidationInfo,
        parkingFeeSekPerDay: parkingFeeSekPerDayValidationInfo,
        congestionTaxSekPerDay: congestionTaxSekPerDayValidationInfo,
        travelDeductionSekPerYear: travelDeductionSekPerYearValidationInfo,
      });
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo(
        `Travel information component - `,
        errors
          .filter((e) => e.name === undefined)
          .map((e) => e.errorMessages)
          .reduce((a, b) => a.concat(b), []),
        {}
      );
    },
  }));

  return (
    <div className={`travel-information ${className ?? ''}`}>
      <fieldset className={showDetailsInformation ? 'last-child-margin-bottom-none' : ''}>
        <legend className="visually-hidden">
          {TranslationService.get('pricecalculatorpage.travelinformationheading')}, {TranslationService.get('common.formsrequiredfieldsmessage')}
        </legend>
        <h2 className="mb-1">{TranslationService.get('pricecalculatorpage.travelinformationheading')}</h2>
        <p>{TranslationService.get('common.formsrequiredfieldsmessage')}</p>

        <NumberFieldFormGroup
          inputId={'traveling-days-per-month'}
          required={true}
          labelText={TranslationService.get('pricecalculatorpage.travelinformation.traveldayspermonth')}
          min={0}
          max={31}
          state={{ value: state.value.travelDaysPerMonth, validationInfo: state.validationInfo.children.travelDaysPerMonth ?? new ValidationInfo() }}
          onChange={(value) => {
            onChange?.({ ...state.value, travelDaysPerMonth: value }, TravelInformationField.TravelDaysPerMonth);
          }}
        />
        <div className="row">
          <NumberFieldFormGroup
            inputId={'parking-fee-sek-per-day'}
            className="col-sm-6 mb-sm-1"
            required={false}
            labelText={TranslationService.get('pricecalculatorpage.carinformation.parkingfeesekperday')}
            min={0}
            state={{ value: state.value.parkingFeeSekPerDay, validationInfo: state.validationInfo.children.parkingFeeSekPerDay ?? new ValidationInfo() }}
            onChange={(value) => onChange?.({ ...state.value, parkingFeeSekPerDay: value }, TravelInformationField.parkingFeeSekPerDay)}
          />
          <NumberFieldFormGroup
            inputId={'congestion-tax-sek-per-year'}
            className="col-sm-6"
            required={false}
            labelText={TranslationService.get('pricecalculatorpage.carinformation.congestiontaxsekperday')}
            ariaDescribedby="congestion-tax-help-text"
            min={0}
            state={{ value: state.value.congestionTaxSekPerDay, validationInfo: state.validationInfo.children.congestionTaxSekPerDay ?? new ValidationInfo() }}
            onChange={(value) => onChange?.({ ...state.value, congestionTaxSekPerDay: value }, TravelInformationField.CongestionTaxSekPerMonth)}
            helpTextElement={
              <div
                id="congestion-tax-help-text"
                dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('pricecalculatorpage.travelinformation.congestiontaxinformation').replace(/<\/?p>/g, '')) }}
              ></div>
            }
          />
        </div>
        <NumberFieldFormGroup
          inputId={'distance-from-home-to-work-km'}
          required={true}
          labelText={TranslationService.get('pricecalculatorpage.travelinformation.distancefromhometowork')}
          min={0}
          state={{ value: state.value.distanceFromHomeToWorkKmState, validationInfo: state.validationInfo.children.distanceFromHomeToWorkKm ?? new ValidationInfo() }}
          onChange={(value) => {
            onChange?.({ ...state.value, distanceFromHomeToWorkKmState: value }, TravelInformationField.DistanceFromHomeToWorkKmState);
          }}
        />
        <CheckboxFormGroup
          id="travel-zones"
          labelText={TranslationService.get('pricecalculatorpage.travelinformation.travelzones')}
          ariaDescribedby="zone-information-help-text"
          required={true}
          options={[
            { value: ZoneType.ZoneA, text: 'Zon A' },
            { value: ZoneType.ZoneB, text: 'Zon B' },
            { value: ZoneType.ZoneC, text: 'Zon C' },
          ]}
          state={{
            value: state.value.travelZones,
            validationInfo: state.validationInfo.children.travelZones ?? new ValidationInfo(),
          }}
          onChange={(value) => {
            AnalyticsService.addEvent(AnalyticsService.PriceCalculator, 'Val av zon', value.toString());
            onChange?.({ ...state.value, travelZones: value as ZoneType[] }, TravelInformationField.TravelZones);
          }}
          helpTextElement={
            <div
              id="zone-information-help-text"
              className="last-child-margin-bottom-none"
              dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('pricecalculatorpage.zoneinformation')) }}
            ></div>
          }
        />
        <SelectFormGroup
          id="ticket-type"
          labelText={TranslationService.get('pricecalculatorpage.travelinformation.preferredtickettype')}
          options={[
            { value: TicketType.SingleTicket, text: TranslationService.get('pricecalculatorpage.tickettype.singleticket') },
            { value: TicketType.DayTicket, text: TranslationService.get('pricecalculatorpage.tickettype.dayticket24hours') },
            { value: TicketType.ThreeDayTicket, text: TranslationService.get('pricecalculatorpage.tickettype.dayticket72hours') },
            { value: TicketType.ThirtyDays, text: TranslationService.get('pricecalculatorpage.tickettype.periodticket30days') },
            { value: TicketType.NinetyDays, text: TranslationService.get('pricecalculatorpage.tickettype.periodticket90days') },
            { value: TicketType.ThreeHundredSixtyFiveDays, text: TranslationService.get('pricecalculatorpage.tickettype.periodticket365days') },
          ]}
          className={!showDetailsInformation ? 'mb-0' : ''}
          state={{
            value: state.value.prefferredTicketType,
            validationInfo: state.validationInfo.children.prefferredTicketType ?? new ValidationInfo(),
          }}
          onChange={(value) => {
            AnalyticsService.addEvent(AnalyticsService.PriceCalculator, 'Val av biljett', value.toString());
            onChange?.({ ...state.value, prefferredTicketType: value as TicketType }, TravelInformationField.PrefferredTicketType);
          }}
        />
        {showDetailsInformation && (
          <NumberFieldFormGroup
            inputId={'travel-deduction-sek-per-year'}
            required={false}
            labelText={TranslationService.get('pricecalculatorpage.carinformation.traveldeductionsekperyear')}
            min={0}
            state={{ value: state.value.travelDeductionSekPerYear, validationInfo: state.validationInfo.children.travelDeductionSekPerYear ?? new ValidationInfo() }}
            onChange={(value) => onChange?.({ ...state.value, travelDeductionSekPerYear: value }, TravelInformationField.TravelDeductionSekPerYear)}
          />
        )}
      </fieldset>
    </div>
  );
});
